import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fishBadge } from "@/lib/api";
import { useCatches } from "@/lib/catches";
import { CatchForm } from "@/components/CatchForm";
import { BookOpen, Trash2, Trophy, Fish, Hash, MapPin, Calendar } from "lucide-react";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl bg-white border border-emerald-900/10 p-4 flex items-center gap-3">
    <Icon className="w-6 h-6 text-emerald-600 shrink-0" />
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wide text-emerald-700/70">{label}</p>
      <p className="font-display font-bold text-emerald-950 truncate" data-testid={testid}>{value}</p>
    </div>
  </div>
);

const stats = (catches) => {
  const biggest = catches.filter((c) => c.weight).sort((a, b) => b.weight - a.weight)[0];
  const counts = catches.reduce((acc, c) => {
    const k = c.species.charAt(0).toUpperCase() + c.species.slice(1).toLowerCase();
    return { ...acc, [k]: (acc[k] || 0) + 1 };
  }, {});
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return { biggest: biggest ? `${biggest.species} · ${biggest.weight} kg` : "—", top: top ? top[0] : "—" };
};

export default function CatchLogPage() {
  const [params] = useSearchParams();
  const { catches, add, remove } = useCatches();
  const [spots, setSpots] = useState([]);
  const [fishList, setFishList] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/spots").then((r) => setSpots(r.data));
    api.get("/filters").then((r) => setFishList(r.data.fish));
  }, []);

  const s = stats(catches);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12" data-testid="catch-log-page">
      <p className="text-xs font-semibold tracking-widest uppercase text-emerald-700/80 flex items-center gap-2">
        <BookOpen className="w-4 h-4" /> Mon carnet de prises
      </p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-emerald-950 mt-2">Vos plus belles prises</h1>
      <p className="text-slate-600 mt-3 max-w-2xl">Notez chaque poisson. Votre carnet reste privé, enregistré uniquement sur cet appareil.</p>

      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        <Stat icon={Hash} label="Prises" value={catches.length} testid="stat-total" />
        <Stat icon={Trophy} label="Plus gros poisson" value={s.biggest} testid="stat-biggest" />
        <Stat icon={Fish} label="Espèce favorite" value={s.top} testid="stat-top" />
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-8 mt-8">
        {spots.length > 0 && <CatchForm spots={spots} fishList={fishList} defaultSpot={params.get("spot")} onAdd={add} />}
        <div className="space-y-3" data-testid="catch-list">
          {catches.length === 0 && (
            <div className="rounded-3xl border-2 border-dashed border-emerald-900/15 p-12 text-center text-slate-500" data-testid="catch-empty">
              <Fish className="w-10 h-10 mx-auto text-emerald-300 mb-2" />
              Aucune prise pour l'instant. Ajoutez votre première !
            </div>
          )}
          {catches.map((c) => (
            <div key={c.id} data-testid={`catch-item-${c.id}`} className="rounded-2xl bg-white border border-emerald-900/10 p-5 flex gap-4 animate-float-up">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-sm font-bold px-3 py-1 rounded-full ${fishBadge(c.species)}`}>{c.species}</span>
                  {c.weight != null && <span className="font-mono text-sm font-bold text-emerald-900">{c.weight} kg</span>}
                  {c.length != null && <span className="font-mono text-sm text-slate-600">{c.length} cm</span>}
                </div>
                <p className="text-xs text-slate-500 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(c.date).toLocaleDateString("fr-FR")}</span>
                  {c.place && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {c.spotId ? <Link to={`/spots/${c.spotId}`} className="underline hover:text-emerald-700">{c.place}</Link> : c.place}
                    </span>
                  )}
                  {c.bait && <span>· {c.bait}</span>}
                </p>
                {c.notes && <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{c.notes}</p>}
              </div>
              <button onClick={() => remove(c.id)} data-testid={`catch-delete-${c.id}`} title="Supprimer" className="p-2 h-fit rounded-full text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
